import { useCallback, useState } from 'react'
import { MAX_PANELS, type OpenPanel, type PanelId } from './panels/types'

/**
 * Which instruments are open.
 *
 * The eviction rule is the whole file: at the cap, a new panel closes the
 * oldest AGENT-opened one. If every open panel is yours, the new one is
 * refused rather than taking one of yours away. An assistant that closes
 * the thing you were reading in order to show you something else is worse
 * than one that waits.
 */

/** Pure, so the rule is readable and testable without React. */
export function openPanel(
  now: readonly OpenPanel[],
  id: PanelId,
  by: 'you' | 'agent',
  at: number,
): OpenPanel[] {
  if (now.some((panel) => panel.id === id)) return [...now]
  const next = [...now, { id, by, at }]
  if (next.length <= MAX_PANELS) return next
  const evictable = next
    .filter((panel) => panel.by === 'agent' && panel.id !== id)
    .sort((a, b) => a.at - b.at)[0]
  // Every open panel is theirs: refuse rather than close one of them.
  if (!evictable) return [...now]
  return next.filter((panel) => panel.id !== evictable.id)
}

export function usePanels(): {
  open: OpenPanel[]
  isOpen: (id: PanelId) => boolean
  toggle: (id: PanelId) => void
  show: (id: PanelId, by?: 'you' | 'agent') => void
  close: (id: PanelId) => void
  closeAll: () => void
} {
  const [open, setOpen] = useState<OpenPanel[]>([])

  const show = useCallback((id: PanelId, by: 'you' | 'agent' = 'you') => {
    setOpen((now) => openPanel(now, id, by, Date.now()))
  }, [])

  const close = useCallback((id: PanelId) => {
    setOpen((now) => now.filter((panel) => panel.id !== id))
  }, [])

  const toggle = useCallback((id: PanelId) => {
    setOpen((now) =>
      now.some((panel) => panel.id === id)
        ? now.filter((panel) => panel.id !== id)
        : openPanel(now, id, 'you', Date.now()),
    )
  }, [])

  const closeAll = useCallback(() => {
    setOpen([])
  }, [])

  const isOpen = useCallback(
    (id: PanelId) => open.some((panel) => panel.id === id),
    [open],
  )

  return { open, isOpen, toggle, show, close, closeAll }
}
